import { useCallback, useState } from 'react'

import { describeError, fieldOfError } from '../lib/api/messages.ts'
import { auth } from '../lib/api/resources.ts'
import { PASSWORD_MIN_LENGTH } from '../lib/api/types.ts'
import type { SessionResponse } from '../lib/api/types.ts'
import { useAuth } from '../lib/auth/AuthContext.tsx'
import { formatDayClock } from '../lib/format.ts'
import { useAction } from '../lib/useAction.ts'
import { useResource } from '../lib/useResource.ts'
import { PageHeader } from '../components/layout/PageHeader.tsx'
import { Badge, RoleBadge } from '../components/primitives/Badge.tsx'
import { Button } from '../components/primitives/Button.tsx'
import { ConfirmDialog } from '../components/primitives/Dialog.tsx'
import { TextField } from '../components/primitives/Field.tsx'
import { ErrorState, LoadingRow, Notice } from '../components/primitives/States.tsx'
import { TABLE, TD, TH, THEAD, TR } from '../components/primitives/table.ts'
import { useToast } from '../components/primitives/Toast.tsx'

/**
 * Minha conta.
 *
 * Duas coisas que só o dono da conta faz: trocar a própria senha (exigindo a
 * atual) e olhar de onde a conta está logada.
 */
export function Account() {
  const { user, isTenant, companyName } = useAuth()

  return (
    <>
      <PageHeader title="Minha conta" />

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="max-w-3xl space-y-5 p-3">
          {user !== null && (
            <section className="border border-line bg-surface p-3">
              <h2 className="mb-2 text-micro font-medium uppercase tracking-label text-fg-secondary">
                Identidade
              </h2>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                <dt className="text-fg-muted">Nome</dt>
                <dd className="text-fg">{user.displayName}</dd>
                <dt className="text-fg-muted">Login</dt>
                <dd className="font-mono text-fg">{user.username}</dd>
                <dt className="text-fg-muted">E-mail</dt>
                <dd className="text-fg-secondary">{user.email}</dd>
                {/* A empresa só existe no escopo `tenant`; a Black Ghost não tem empresa. */}
                <dt className="text-fg-muted">{isTenant ? 'Empresa' : 'Organização'}</dt>
                <dd className="text-fg">{isTenant ? (companyName ?? 'empresa não resolvida') : 'Black Ghost'}</dd>
                <dt className="text-fg-muted">Papéis</dt>
                <dd className="flex flex-wrap gap-1">
                  {user.roles.map((role) => (
                    <RoleBadge key={role} role={role} />
                  ))}
                </dd>
                <dt className="text-fg-muted">Último acesso</dt>
                <dd className="tnum text-fg-secondary">{formatDayClock(user.lastLoginAt)}</dd>
              </dl>
              {/* O papel exibido vem do servidor a cada carga, não de algo que o
                  cliente guardou: esconder um botão no front é conveniência, a
                  autorização de verdade acontece em cada endpoint. */}
            </section>
          )}

          <ChangePassword />
          <Sessions />
        </div>
      </div>
    </>
  )
}

/**
 * Trocar a própria senha.
 *
 * Exige a senha atual mesmo com sessão válida: sem isso, um token roubado
 * bastaria para o atacante trocar a senha e expulsar o dono da própria conta.
 *
 * E, depois do `204`, **todas** as sessões caem — inclusive esta. Os cookies já
 * foram apagados e o token em memória não renova mais, então a única coisa
 * honesta a fazer é levar o operador ao login. Continuar na tela produziria um
 * 401 no próximo clique, que pareceria bug.
 */
export function ChangePassword({ firstAccess = false }: { firstAccess?: boolean } = {}) {
  const toast = useToast()
  const { endSession } = useAuth()
  const action = useAction()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')

  const field = fieldOfError(action.error)
  const message = describeError(action.error)
  const short = newPassword.length > 0 && newPassword.length < PASSWORD_MIN_LENGTH

  return (
    <section className="border border-line bg-surface p-3">
      <h2 className="mb-2 text-micro font-medium uppercase tracking-label text-fg-secondary">
        {firstAccess ? 'Defina a sua senha' : 'Trocar a senha'}
      </h2>

      <form
        className="max-w-sm space-y-3"
        onSubmit={(event) => {
          event.preventDefault()
          action.run(async () => {
            await auth.changePassword({ currentPassword, newPassword })
            toast.success('Senha trocada. Entre de novo com a senha nova.')
            endSession()
          })
        }}
      >
        <TextField
          label={firstAccess ? 'Senha provisória (do e-mail)' : 'Senha atual'}
          type="password"
          value={currentPassword}
          onValueChange={setCurrentPassword}
          autoComplete="current-password"
          error={field === 'currentPassword' ? message : null}
          required
        />

        <TextField
          label="Nova senha"
          type="password"
          value={newPassword}
          onValueChange={setNewPassword}
          autoComplete="new-password"
          error={field === 'password' ? message : null}
          hint={
            <span className={short ? 'text-warn' : undefined}>
              {newPassword.length} de {PASSWORD_MIN_LENGTH} caracteres no mínimo.
            </span>
          }
          required
        />

        <Notice tone="warn">
          Todas as suas sessões caem, inclusive esta.
        </Notice>

        {field === null && action.error !== null && (
          <p role="alert" className="text-sm text-crit">
            {message}
          </p>
        )}

        <Button type="submit" variant="primary" busy={action.busy}>
          Trocar senha
        </Button>
      </form>
    </section>
  )
}

/**
 * Sessões ativas.
 *
 * O teto é de 10 por usuário: passando dele, a mais antiga cai sozinha. Sem
 * teto, cada login deixaria para trás uma sessão de 30 dias e esta lista viraria
 * um histórico de logins em vez de uma lista de dispositivos.
 */
function Sessions() {
  const toast = useToast()
  const { endSession } = useAuth()
  const action = useAction()
  const [pending, setPending] = useState<SessionResponse | null>(null)

  const fetcher = useCallback((signal: AbortSignal) => auth.sessions(signal), [])
  const { data, error, loading, reload } = useResource(fetcher)

  return (
    <section>
      <div className="mb-1 flex items-center justify-between gap-3">
        <h2 className="text-micro font-medium uppercase tracking-label text-fg-secondary">
          Sessões ativas
        </h2>
        <Button size="sm" onClick={reload} busy={loading}>
          atualizar
        </Button>
      </div>

      {error !== null ? (
        <ErrorState error={error} onRetry={reload} />
      ) : data === null ? (
        <LoadingRow />
      ) : (
        <table className={TABLE}>
          <thead className={THEAD}>
            <tr>
              <th scope="col" className={TH}>
                Dispositivo
              </th>
              <th scope="col" className={TH}>
                Endereço
              </th>
              <th scope="col" className={TH}>
                Criada
              </th>
              <th scope="col" className={TH}>
                Último uso
              </th>
              <th scope="col" className={TH}>
                Expira
              </th>
              <th scope="col" className={`${TH} text-right`}>
                <span className="sr-only">Ações</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((session) => (
              <tr key={session.id} className={TR}>
                <td className={`${TD} max-w-0`}>
                  <span className="flex items-center gap-2">
                    {session.isCurrent && <Badge tone="accent">este dispositivo</Badge>}
                    <span className="truncate" title={session.userAgent ?? undefined}>
                      {session.userAgent ?? '—'}
                    </span>
                  </span>
                </td>
                <td className={`${TD} font-mono whitespace-nowrap`}>{session.ipAddress ?? '—'}</td>
                <td className={`${TD} tnum whitespace-nowrap`}>
                  {formatDayClock(session.createdAt)}
                </td>
                <td className={`${TD} tnum whitespace-nowrap`}>
                  {formatDayClock(session.lastUsedAt)}
                </td>
                <td className={`${TD} tnum whitespace-nowrap`}>
                  {formatDayClock(session.expiresAt)}
                </td>
                <td className={`${TD} text-right`}>
                  <Button variant="ghost" size="sm" onClick={() => setPending(session)}>
                    revogar
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pending !== null && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setPending(null)
          }}
          title={pending.isCurrent ? 'Revogar esta sessão?' : 'Revogar a sessão?'}
          // Avisar que é a própria sessão não é firula: sem o aviso, cair no
          // login logo em seguida parece um bug em vez do efeito pedido.
          consequence={
            pending.isCurrent
              ? 'É a sessão atual: você volta para o login.'
              : 'O dispositivo é desconectado na hora e precisa entrar de novo.'
          }
          confirmLabel="Revogar"
          busy={action.busy}
          error={action.error !== null ? describeError(action.error) : null}
          onConfirm={() =>
            action.run(async () => {
              await auth.revokeSession(pending.id)
              setPending(null)

              if (pending.isCurrent) {
                endSession()
                return
              }

              toast.success('Sessão revogada.')
              reload()
            })
          }
        />
      )}
    </section>
  )
}
